import { createPopper, detectOverflow, Placement, Instance as PopperInstance } from '@popperjs/core';
import { Component, Fragment } from 'react';

import { ClassNamed, ElementOrElementGetter, handleRefSplit } from '@optimus-web/common';

import Portal from '../Portal';

import { TargetWrapperElement } from './style';
import { MaxSizeChangeProps, PositioningStrategy } from './types';

export interface PositionerProps extends ClassNamed {
    anchorElement: ElementOrElementGetter;
    children: React.ReactElement;
    opened: boolean;
    offset?: number;
    placement?: Placement;
    positioningStrategy?: PositioningStrategy;
    maxHeight?: number;
    maxWidth?: number;
    boundaryElement?: HTMLElement | null;
    flip?: boolean;
    scrollOnOverflow?: boolean;
    sizeToAnchor?: boolean;
    portalContainerId?: string;
    disablePointerEvents?: boolean;
    onMaxSizeChange?: ({ maxWidth, maxHeight }: MaxSizeChangeProps) => void;
    innerRef?: React.Ref<HTMLDivElement>;
    inPortal?: boolean;
}

export interface PositionerDefaultedProps extends Partial<PositionerProps> {
    anchorElement: ElementOrElementGetter;
    children: React.ReactElement;
}

class Positioner extends Component<PositionerProps> {
    static defaultProps: Partial<PositionerProps> = {
        placement: 'auto',
        positioningStrategy: PositioningStrategy.Fixed,
        offset: 0,
        opened: false,
        flip: true,
        scrollOnOverflow: false,
        sizeToAnchor: false,
        inPortal: true,
    };

    private popperInstance: PopperInstance | null = null;
    private targetElement: HTMLDivElement | null = null;

    componentDidMount() {
        this.updatePlacement();
    }

    componentDidUpdate(prevProps: PositionerProps) {
        const { opened } = this.props;

        // Props that require popper recreation when changed
        const configProps: (keyof PositionerProps)[] = [
            'opened',
            'placement',
            'positioningStrategy',
            'offset',
            'flip',
            'sizeToAnchor',
            'maxHeight',
            'maxWidth',
            'scrollOnOverflow',
            'boundaryElement',
        ];

        const configChanged = configProps.some((prop) => this.props[prop] !== prevProps[prop]);

        if (configChanged) {
            // Recreate popper when configuration changes
            this.updatePlacement();
        } else if (opened && this.popperInstance) {
            // Just update position if already open and config unchanged
            void this.popperInstance.update();
        }
    }

    componentWillUnmount() {
        this.destroyPopperIfExists();
    }

    public getCurrentPlacement = () => {
        if (this.popperInstance) {
            return this.popperInstance.state.placement;
        }
    };

    public updatePlacement = () => {
        const { opened } = this.props;

        this.destroyPopperIfExists();
        if (opened) {
            this.createPopper();
        }
    };

    getAllowedAutoPlacements = (placement?: Placement) => {
        switch (placement) {
            case 'auto':
                return ['bottom', 'top'];
            case 'auto-start':
                return ['bottom-start', 'top-end'];
            case 'auto-end':
                return ['bottom-end', 'top-end'];
            default:
                return [];
        }
    };

    createPopper = () => {
        const {
            offset,
            placement,
            positioningStrategy,
            sizeToAnchor,
            maxHeight,
            maxWidth,
            scrollOnOverflow,
            flip,
            boundaryElement,
        } = this.props;

        const anchorElement = this.getAnchorElement();
        if (!anchorElement) {
            throw Error('Anchor element is not defined');
        }
        if (!this.targetElement) {
            throw Error('Target element is not defined');
        }

        this.popperInstance = createPopper(anchorElement, this.targetElement, {
            placement,
            strategy: positioningStrategy,
            modifiers: [
                {
                    name: 'computeStyles',
                    options: {
                        /* set to false because when user zooms in on browser screen
                        the translate: transform3d made the font look blurry */
                        gpuAcceleration: false,
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {
                        boundary: boundaryElement,
                    },
                },
                {
                    name: 'initialDimensions',
                    enabled: true,
                    phase: 'beforeMain',
                    fn: ({ state }) => {
                        if (maxHeight && state.rects.popper.height > maxHeight) {
                            state.rects.popper.height = maxHeight;
                        }
                        if (maxWidth && state.rects.popper.width > maxWidth) {
                            state.rects.popper.width = maxWidth;
                        }
                    },
                },
                {
                    name: 'flip',
                    enabled: flip,
                    options: {
                        allowedAutoPlacements: this.getAllowedAutoPlacements(placement),
                        boundary: boundaryElement,
                    },
                },
                {
                    name: 'offset',
                    options: {
                        offset: [0, offset],
                    },
                },
                {
                    name: 'sizeToAnchor',
                    enabled: sizeToAnchor,
                    phase: 'beforeWrite',
                    effect: ({ state }) => {
                        state.elements.popper.style.width = `${
                            state.elements.reference.getBoundingClientRect().width
                        }px`;
                        return () => {};
                    },
                },
                {
                    // This is a slightly customized version of the following modifier: https://github.com/atomiks/popper.js/blob/master/src/modifiers/maxSize.js
                    name: 'maxSize',
                    enabled: true,
                    phase: 'beforeWrite',
                    requiresIfExists: ['offset', 'preventOverflow', 'flip'],
                    fn: ({ state, options }) => {
                        const overflow = detectOverflow(state, options);
                        const { x: overflowedX, y: overflowedY } = state.modifiersData.preventOverflow || {
                            x: 0,
                            y: 0,
                        };
                        const { width, height } = state.rects.popper;
                        const [basePopperPlacement] = state.placement.split('-');

                        const widthProp = basePopperPlacement === 'left' ? 'left' : 'right';
                        const heightProp = basePopperPlacement === 'top' ? 'top' : 'bottom';

                        const computedMaxWidth = width - overflow[widthProp] - overflowedX;
                        const computedMaxHeight = height - overflow[heightProp] - overflowedY;

                        const finalMaxWidth = maxWidth ? Math.min(maxWidth, computedMaxWidth) : computedMaxWidth;
                        const finalMaxHeight = maxHeight ? Math.min(maxHeight, computedMaxHeight) : computedMaxHeight;

                        if (maxWidth) {
                            state.elements.popper.style.maxWidth = `${maxWidth}px`;
                        }
                        if (maxHeight) {
                            state.elements.popper.style.maxHeight = `${maxHeight}px`;
                        }

                        if (scrollOnOverflow) {
                            state.elements.popper.style.maxWidth = `${finalMaxWidth}px`;
                            state.elements.popper.style.maxHeight = `${finalMaxHeight}px`;
                            state.elements.popper.style.overflowX = 'auto';
                            state.elements.popper.style.overflowY = 'auto';
                        }

                        const { onMaxSizeChange } = this.props;
                        if (onMaxSizeChange) {
                            onMaxSizeChange({
                                maxWidth: scrollOnOverflow ? finalMaxWidth : maxWidth,
                                maxHeight: scrollOnOverflow ? finalMaxHeight : maxHeight,
                            });
                        }
                    },
                },
            ],
        });
    };

    destroyPopperIfExists = () => {
        if (this.popperInstance) {
            this.popperInstance.destroy();
            this.popperInstance = null;
        }
    };

    getAnchorElement = () => {
        const { anchorElement } = this.props;
        return typeof anchorElement === 'function' ? anchorElement() : anchorElement;
    };

    handleTargetElementRef = (e: HTMLDivElement) => (this.targetElement = e);

    render() {
        const {
            children,
            opened,
            portalContainerId,
            innerRef,
            className,
            disablePointerEvents,
            onMaxSizeChange,
            inPortal,
            ...other
        } = this.props;

        const targetElementProps = {
            ...other,
            className,
            disablePointerEvents,
            ref: handleRefSplit({ ownRef: this.handleTargetElementRef, forwardedRef: innerRef }),
        };

        const WrapperElement = inPortal ? Portal : Fragment;
        const wrapperProps = inPortal ? { containerId: portalContainerId } : {};

        return opened ? (
            <WrapperElement {...wrapperProps}>
                <TargetWrapperElement {...targetElementProps}>{children}</TargetWrapperElement>
            </WrapperElement>
        ) : null;
    }
}

export default Positioner;
